import React, { useState, useCallback, useLayoutEffect, useMemo, useEffect } from 'react';
import './About.css';

/** Interactive timeline — alternating rows, click dot to reveal (styles: Home.css #history) */
const INTERACTIVE_TIMELINE_ENTRIES = [
  {
    yearLabel: 'July 2020',
    text: 'Geomatixs founded its first laboratories in Carmona, Cavite and Sto. Tomas, Batangas. Armed with its core principles of service, value, and excellence — serving clients with a guarantee of satisfaction.',
    dotLines: ['JUL', '2020'],
    aria: 'Show July 2020 details',
  },
  {
    yearLabel: 'April 2021',
    text: 'Opened the 3rd laboratory in General Trias, Cavite — expanding service coverage to cater to more clients\' testing requirements.',
    dotLines: ['APR', '2021'],
    aria: 'Show April 2021 details',
  },
  {
    yearLabel: 'September 2021',
    text: 'Introduced the country\'s newest technology of the electric density gauge — rapidly accomplishing field density tests with great accuracy.',
    dotLines: ['SEP', '2021'],
    aria: 'Show September 2021 details',
  },
  {
    yearLabel: 'March 2022',
    text: 'Opened the 4th laboratory in Silang–Tagaytay, Cavite to serve the growing industry in the district.',
    dotLines: ['MAR', '2022'],
    aria: 'Show March 2022 details',
  },
  {
    yearLabel: 'March 2023',
    text: 'Opened the 5th laboratory in Tanza, Cavite to further serve clients in Kawit, Naic, and Tanza.',
    dotLines: ['MAR', '2023'],
    aria: 'Show March 2023 details',
  },
  {
    yearLabel: 'February 2024',
    text: 'Opened the 6th laboratory in Lima Tech, Lipa, Batangas to cater to more clients in the Batangas area.',
    dotLines: ['FEB', '2024'],
    aria: 'Show February 2024 details',
  },
  {
    yearLabel: 'March 2025',
    text: 'Opened the in-house laboratory at Banlic, Calamba, Laguna for the NSCR CP-SO7 project — LOTTE–GULERMAK–EEI Joint Venture.',
    dotLines: ['MAR', '2025'],
    aria: 'Show March 2025 details',
  },
  {
    yearLabel: 'September 2025',
    text: 'Opened the 7th laboratory in Balayan, Batangas — now 60 technical personnel deployed across 7 laboratories and 1 in-house facility.',
    dotLines: ['SEP', '2025'],
    aria: 'Show September 2025 details',
  },
];

/** First index not yet opened (chronological order). All open → null. */
function getNextTimelineIndex(open) {
  for (let i = 0; i < INTERACTIVE_TIMELINE_ENTRIES.length; i++) {
    if (!open.has(i)) return i;
  }
  return null;
}

const About = ({ scrollToResourcesInitially, onResourcesScrollDone }) => {
  const [openTimelineRows, setOpenTimelineRows] = useState(() => new Set());
  const [denyShakeIndex, setDenyShakeIndex] = useState(null);

  const nextTimelineIndex = useMemo(() => getNextTimelineIndex(openTimelineRows), [openTimelineRows]);

  useEffect(() => {
    if (denyShakeIndex === null) return undefined;
    const t = setTimeout(() => setDenyShakeIndex(null), 650);
    return () => clearTimeout(t);
  }, [denyShakeIndex]);

  const handleTimelineDotClick = useCallback((index) => {
    setOpenTimelineRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        for (let j = index; j < INTERACTIVE_TIMELINE_ENTRIES.length; j++) {
          next.delete(j);
        }
        return next;
      }
      const mustOpen = getNextTimelineIndex(prev);
      if (index !== mustOpen) {
        queueMicrotask(() => setDenyShakeIndex(index));
        return prev;
      }
      next.add(index);
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    if (!scrollToResourcesInitially) return;
    const el = document.getElementById('resources');
    if (el) {
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    onResourcesScrollDone?.();
  }, [scrollToResourcesInitially, onResourcesScrollDone]);

  return (
    <div className="about-page">
      <section id="about" className="about-page-hero" aria-labelledby="about-page-title">
        <div className="about-page-hero__inner">
          <p className="about-page-eyebrow">Geomatixs Group Corp.</p>
          <h1 id="about-page-title" className="about-page-title">
            About us
          </h1>
          <p className="about-page-lead">
            Independent engineering laboratory services for construction, infrastructure, and industrial projects — field and laboratory testing
            you can document and defend.
          </p>
        </div>
      </section>

      <section className="about-page-story about-info" aria-labelledby="about-story-heading">
        <div className="container about-page-container">
          <header className="about-info-header">
            <p className="about-info-kicker">At a glance</p>
            <h2 id="about-story-heading" className="about-info-title">
              Company overview
            </h2>
            <p className="about-info-dek">
              GEOMATIXS GROUP CORP. — engineering and technical testing for construction, infrastructure, and industrial clients across Luzon.
            </p>
          </header>

          <div className="about-info-split">
            <figure className="about-info-media">
              <img
                src={`${process.env.PUBLIC_URL}/bgphoto.png`}
                alt="Geomatixs laboratory and field testing operations"
                className="about-info-media__img"
                loading="lazy"
                decoding="async"
              />
              <figcaption className="about-info-media__cap">Laboratory &amp; field operations</figcaption>
            </figure>

            <div className="about-info-panel">
              <h3 className="about-info-panel__title">Scope &amp; operations</h3>
              <dl className="about-info-facts">
                <div className="about-info-fact">
                  <dt>Network</dt>
                  <dd>Seven (7) regional laboratories plus one (1) in-house project laboratory (e.g. Banlic, Calamba — project-specific).</dd>
                </div>
                <div className="about-info-fact">
                  <dt>Markets</dt>
                  <dd>Industrial, commercial, residential, and public works — geotechnical investigation, construction materials testing, and related field services.</dd>
                </div>
                <div className="about-info-fact">
                  <dt>Delivery</dt>
                  <dd>Documented procedures, calibrated equipment, and trained technical personnel for repeatable, reportable results.</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section
        id="history"
        className="section about-timeline-section about-content about-interactive-timeline section-animate anim-fade section-visible"
        aria-labelledby="timeline-heading"
      >
        <div className="container">
          <header className="about-timeline-section__header reveal">
            <div className="section-eyebrow">Our journey</div>
            <h2 id="timeline-heading" className="section-title history-timeline-title">
              Brief <span className="yellow">history</span>
            </h2>
            <p className="section-lead">
              <b>
                From our first laboratories in 2020 to seven regional labs and dedicated project facilities — built on service, value, and
                excellence.
              </b>
            </p>
            <p className="timeline-click-hint">
              Open each date in order — start from the top
              <span className="arrow" aria-hidden="true">
                ↓
              </span>
            </p>
          </header>
          <div className="timeline reveal">
            {INTERACTIVE_TIMELINE_ENTRIES.map((entry, index) => {
              const isOpen = openTimelineRows.has(index);
              const isOddRow = index % 2 === 0;
              const isLocked = !isOpen && nextTimelineIndex !== null && index !== nextTimelineIndex;
              const isCallout = !isOpen && nextTimelineIndex === index;
              const dotClass = [
                'tl-dot',
                'tl-dot-btn',
                isLocked ? 'tl-dot-btn--locked' : '',
                isCallout ? 'tl-dot-btn--callout' : '',
                denyShakeIndex === index ? 'tl-dot-btn--shake-once' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const contentBlock = (
                <div className="tl-content">
                  <div className="tl-year">{entry.yearLabel}</div>
                  <div className="tl-text">{entry.text}</div>
                </div>
              );
              const dotBlock = (
                <div className="tl-spacer">
                  <button
                    type="button"
                    className={dotClass}
                    aria-expanded={isOpen}
                    aria-label={
                      isLocked
                        ? `${entry.aria} — open the previous date on the line first`
                        : entry.aria
                    }
                    onClick={() => handleTimelineDotClick(index)}
                  >
                    {entry.dotLines[0]}
                    <br />
                    {entry.dotLines[1]}
                  </button>
                </div>
              );
              const emptyBlock = <div className="tl-empty" aria-hidden="true" />;
              return (
                <div key={entry.yearLabel} className={`timeline-item ${isOpen ? 'open' : ''}`}>
                  {isOddRow ? (
                    <>
                      {contentBlock}
                      {dotBlock}
                      {emptyBlock}
                    </>
                  ) : (
                    <>
                      {emptyBlock}
                      {dotBlock}
                      {contentBlock}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="resources" className="section resources-section section-animate anim-scale section-visible about-page-resources">
        <div className="container">
          <h2 className="section-title about-page-resources__title">Downloadable resources</h2>
          <div className="resources-card about-page-resources__card">
            <div className="resources-content">
              <div className="resources-info">
                <div className="icon-container">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div className="text-content">
                  <h3>Company profile</h3>
                  <p>
                    Download our company profile for services, credentials, and organizational information (PDF).
                  </p>
                </div>
              </div>
              <div className="download-button-container">
                <a
                  href={`${(process.env.PUBLIC_URL || '').replace(/^\.\/?$/, '')}/GGC-Company-Profile-March-2026.pdf`.replace(/^\.\/?/, '/')}
                  download="Company Profile (March-2026).pdf"
                  className="download-btn btn-animate"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>Download PDF</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
